import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { AuthProvider } from './contexts/AuthContext';
import { register as registerServiceWorker } from './serviceWorkerRegistration';
import { installClientErrorReporter } from './services/clientErrorReporter';

// Client error reporting (always-on): batches window errors, unhandled
// rejections, and boundary catches to /api/client-errors, where they land in
// api_error_log with the spike-alerting cron. This MUST run before the
// suppression guards below so it sees every event first, even ones a later
// capture-phase guard stops.
installClientErrorReporter();

// Optional Sentry when a DSN is configured. vite.config.js defines the
// REACT_APP_SENTRY_DSN key unconditionally so this reference can never crash
// the bundle when the env file omits it.
try {
  const DSN = process.env.REACT_APP_SENTRY_DSN;
  if (DSN) {
    // dynamic import to avoid bundling if not used
    import('@sentry/react').then((Sentry) => {
      Sentry.init({
        dsn: DSN,
        environment: process.env.NODE_ENV,
        tracesSampleRate: 0.05,
        beforeSend(event) {
          // scrub PII
          if (event.request) {
            delete event.request.data;
            delete event.request.headers;
          }
          return event;
        },
      });
    });
  }
} catch (e) { /* no-op */ }

// Check and output diagnostic reload reasoning
try {
  if (typeof window !== 'undefined') {
    const reasonStr = window.localStorage.getItem('fs_reload_reason');
    if (reasonStr) {
      window.localStorage.removeItem('fs_reload_reason');
      const reason = JSON.parse(reasonStr);
      console.error(
        `%c🔄 STAGSTER Automatic Reload Diagnostic %c\n` +
        `The page was automatically reloaded at ${reason.time} on URL: ${reason.url}\n` +
        `Triggered by error: "${reason.error}"`,
        'background: #7c3aed; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
        'color: inherit;'
      );
    }
  }
} catch (_) {}


const clearLocalRuntimeCaches = async () => {
  if (typeof window === 'undefined') return;
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  if (!isLocalhost) return;

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch (_) {
    // no-op
  }

  try {
    if ('caches' in window) {
      const cacheNames = await window.caches.keys();
      await Promise.all(cacheNames.map((name) => window.caches.delete(name)));
    }
  } catch (_) {
    // no-op
  }
};

const CHUNK_RELOAD_GUARD = 'fs_chunk_reload_once';
const isChunkLoadFailure = (value) => {
  const message = typeof value === 'string' ? value : value?.message || '';
  const name = typeof value === 'object' && value ? value.name || '' : '';
  return (
    name === 'ChunkLoadError' ||
    message.includes('ChunkLoadError') ||
    message.includes('Loading chunk') ||
    message.includes('failed dynamically imported module') ||
    message.includes('Failed to fetch dynamically imported module') ||
    (message.includes('module script') && message.includes('text/html'))
  );
};

const reloadForChunkMismatch = (reason) => {
  try {
    const errorStr = typeof reason === 'string'
      ? reason
      : reason?.message || reason?.error?.message || String(reason || '');
    window.localStorage.setItem('fs_reload_reason', JSON.stringify({
      error: errorStr,
      time: new Date().toISOString(),
      url: window.location.href
    }));
  } catch (_) {}

  try {
    if (window.sessionStorage.getItem(CHUNK_RELOAD_GUARD) === '1') {
      return false;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_GUARD, '1');
  } catch (_) {
    // no-op
  }
  window.location.reload();
  return true;
};

// Ensure browser tab title is updated consistently
try {
  const NEW_TITLE = 'STAGSTER - Built in India, for Solos Everywhere';
  if (typeof document !== 'undefined' && document.title !== NEW_TITLE) {
    document.title = NEW_TITLE;
  }
} catch (_) { /* no-op */ }

// Comprehensive ResizeObserver error suppression
const originalError = console.error;
const originalWarn = console.warn;

console.error = (...args) => {
  const message = args[0];
  if (typeof message === 'string' && (
    message.includes('ResizeObserver loop completed with undelivered notifications') ||
    message.includes('ResizeObserver loop limit exceeded')
  )) {
    return;
  }
  // Suppress fastlane 403 network errors from browser console
  if (typeof message === 'string' && message.includes('fastlane') && message.includes('403')) {
    return;
  }
  if (typeof message === 'string' && message.includes('403') && message.includes('Forbidden') && message.includes('fastlane')) {
    return;
  }
  // Suppress browser network logs for fastlane 403s
  const fullMessage = args.map(a => String(a)).join(' ');
  if (fullMessage.includes('fastlane/request/status') && fullMessage.includes('403')) {
    return;
  }
  originalError.apply(console, args);
};

console.warn = (...args) => {
  const message = args[0];
  if (typeof message === 'string' && message.includes('ResizeObserver')) {
    return;
  }
  // Suppress referral code warnings - feature may be disabled or user not authenticated
  if (typeof message === 'string' && (message.includes('Failed to load referral code') || message.includes('referral code'))) {
    return;
  }
  // Suppress backend API check warnings (proxy/network errors when backend is starting)
  if (typeof message === 'string' && message.includes('Backend API check')) {
    return;
  }
  originalWarn.apply(console, args);
};

// Global error handlers. Chunk failures reload once; ResizeObserver noise is
// a browser quirk; OAuth PKCE and fastlane 403s are expected app flows.
// Generic network failures (403/500/Failed to fetch/etc.) are deliberately
// NOT suppressed here anymore: the reporter installed above forwards them to
// /api/client-errors (throttled server-side), so real outages are visible
// instead of vanishing into the console.
window.addEventListener('error', (event) => {
  if (isChunkLoadFailure(event?.error) || isChunkLoadFailure(event?.message)) {
    event.preventDefault();
    reloadForChunkMismatch(event?.error || event?.message || 'Unknown error');
    return false;
  }
  if (event.message && event.message.includes('ResizeObserver')) {
    event.preventDefault();
    return false;
  }
  // Expected flows only: OAuth PKCE exchange errors and fastlane 403 probes.
  if (event.message && (
    (event.message.includes('fastlane') && event.message.includes('403')) ||
    event.message.includes('code verifier') ||
    event.message.includes('code_verifier') ||
    (event.message.includes('OAuth exchange') && event.message.includes('validation_failed'))
  )) {
    event.preventDefault();
    return false;
  }
});

// Suppress only expected-flow unhandled rejections; everything else reaches
// both the console and the error reporter.
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const message = typeof reason === 'string' ? reason : reason?.message || '';
  if (isChunkLoadFailure(reason) || isChunkLoadFailure(message)) {
    event.preventDefault();
    reloadForChunkMismatch(reason || message || 'Unknown rejection');
    return false;
  }
  if (
    (message.includes('fastlane') && message.includes('403')) ||
    message.includes('code verifier') ||
    message.includes('code_verifier') ||
    (message.includes('OAuth exchange') && message.includes('validation_failed'))
  ) {
    event.preventDefault();
    return false;
  }
});

// Capture-phase guards to stop React overlay from seeing these
const isRO = (txt) => typeof txt === 'string' && (
  txt.includes('ResizeObserver loop completed with undelivered notifications') ||
  txt.includes('ResizeObserver loop limit exceeded') ||
  txt.includes('ResizeObserver')
);

window.addEventListener('error', (event) => {
  if (isChunkLoadFailure(event?.error) || isChunkLoadFailure(event?.message)) {
    try { event.stopImmediatePropagation(); } catch(_) {}
    event.preventDefault();
    reloadForChunkMismatch(event?.error || event?.message || 'Unknown error');
    return false;
  }
  if (isRO(event?.message)) {
    try { event.stopImmediatePropagation(); } catch(_) {}
    event.preventDefault();
    return false;
  }
}, true);

window.addEventListener('unhandledrejection', (event) => {
  const reason = event?.reason;
  const msg = (reason && (reason.message || (typeof reason === 'string' ? reason : ''))) || '';
  if (isChunkLoadFailure(reason) || isChunkLoadFailure(msg)) {
    try { event.stopImmediatePropagation(); } catch(_) {}
    event.preventDefault();
    reloadForChunkMismatch(reason || msg || 'Unknown rejection');
    return false;
  }
  if (isRO(msg)) {
    try { event.stopImmediatePropagation(); } catch(_) {}
    event.preventDefault();
    return false;
  }
  // Suppress only expected-flow rejections (OAuth PKCE, fastlane 403s) from
  // reaching the dev overlay; the reporter above has already recorded them.
  if (
    (msg.includes('fastlane') && msg.includes('403')) ||
    msg.includes('code verifier') ||
    msg.includes('code_verifier') ||
    (msg.includes('OAuth exchange') && msg.includes('validation_failed'))
  ) {
    try { event.stopImmediatePropagation(); } catch(_) {}
    event.preventDefault();
    return false;
  }
}, true);

// Global ResizeObserver error handler (onerror)
try {
  const prev = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    try {
      if (String(message || '').includes('ResizeObserver')) {
        return true; // swallow
      }
    } catch(_) {}
    if (typeof prev === 'function') {
      return prev(message, source, lineno, colno, error);
    }
    return false;
  };
} catch(_) {}

// Conditionally expose globals for development only
const isDevelopment = process.env.NODE_ENV === 'development';
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

if (isDevelopment || isLocalhost) {
  // Expose React globally for diagnostic scripts and debugging
  window.React = React;
  window.ReactDOM = ReactDOM;

  // Dev-only: patch ResizeObserver to schedule via rAF and avoid loop-limit overlays
  try {
    if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
      const OriginalRO = window.ResizeObserver;
      window.ResizeObserver = class PatchedRO {
        constructor(callback) {
          this._ro = new OriginalRO((entries, obs) => {
            // schedule DOM reads/writes next frame to avoid synchronous loops
            window.requestAnimationFrame(() => {
              try { callback(entries, obs); } catch (_) {}
            });
          });
        }
        observe(...args) { try { this._ro.observe(...args); } catch(_) {} }
        unobserve(...args) { try { this._ro.unobserve(...args); } catch(_) {} }
        disconnect(...args) { try { this._ro.disconnect(...args); } catch(_) {} }
      };
    }
  } catch (_) {}

  // MutationObserver to hide dev overlay only for benign ResizeObserver messages
  try {
    const mo = new MutationObserver(() => {
      try {
        const overlay = document.querySelector('#webpack-dev-server-client-overlay, .webpack-dev-server-client-overlay');
        if (!overlay) return;
        const txt = (overlay.textContent || '').toString();
        if (txt.includes('ResizeObserver')) {
          overlay.style.display = 'none';
        }
      } catch (_) {}
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  } catch (_) {}

  console.log('🔧 Development mode: Global objects exposed for debugging');
} else {
  console.log('🚀 Production mode: Debug globals hidden');
}

const root = ReactDOM.createRoot(document.getElementById("root"));
clearLocalRuntimeCaches().finally(() => {
  root.render(
    <React.StrictMode>
      <AuthProvider>
        <App />
      </AuthProvider>
    </React.StrictMode>,
  );
});

// Register the service worker for Web Push + offline shell.
// (Previously the app unregistered it on mount, which broke push entirely.)
registerServiceWorker();
